/**
 * How a chart renders a value that isn't there.
 *
 * The reports deliberately send `null` for a metric that doesn't apply to a given day — a CPA whose
 * conversion count rounds to zero (PRD-203) — so the line breaks into a gap rather than dropping to
 * a misleading $0. ECharts does not hand that null back to a formatter: it parses null, undefined,
 * "" and "-" to NaN when storing a numeric dimension (see its dataValueHelper#parseDataValue), so a
 * formatter guarding only on `value == null` still renders "NaN" to the reader.
 *
 * ECharts' own default formatter guards with exactly this finite check (isNullableNumberFinite:
 * `val != null && isFinite(val)`). Ours replace that default, so they have to carry the guard too —
 * which is why it lives here rather than being repeated per chart, where one copy would drift.
 */

// Absent reads as the em dash Reporting::V2::Metric.format_value uses, so a chart and the table
// under it agree on what "no value" looks like.
export const ABSENT_VALUE = "—"

// Absent covers null/undefined (nothing sent, or an index past the end of a series), NaN (what
// ECharts turns those into), ±Infinity (a ratio over a zero denominator), and "" — which has to be
// named explicitly, because Number("") is 0 and would otherwise print as a real $0. A genuine 0 is
// NOT absent: it formats normally, since zero spend on a paused day is information.
export function isAbsent(value) {
  return value == null || value === "" || !Number.isFinite(Number(value))
}
